'use client';

import { useEffect, useRef, useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface DynamicStat {
  value: string;
  label: string;
  icon: string;
  isStatic?: boolean;
  numericValue?: number;
}

interface SocialProofSectionProps {
  dynamicStats?: DynamicStat[];
  statsLoading?: boolean;
}

const DEFAULT_STATS: DynamicStat[] = [
  { value: '0', label: 'Premium Templates', icon: 'RectangleStackIcon', numericValue: 0 },
  { value: '0', label: 'Happy Customers', icon: 'UsersIcon', numericValue: 0 },
  { value: '6', label: 'Platforms Supported', icon: 'CubeTransparentIcon', isStatic: true },
  { value: '0.0/5', label: 'Average Rating', icon: 'StarIcon' },
];

interface DynamicTestimonial {
  id: string;
  name: string;
  role: string;
  avatar: string | null;
  avatarAlt: string;
  quote: string;
  summary: string | null;
  platform: string;
  rating: number;
}

const marqueeItems = [
'Shopify', 'WordPress', 'WooCommerce', 'HTML/CSS', 'Next.js', 'Webflow',
'2,400+ Templates', 'Lifetime Downloads', 'License Keys', 'Dark Mode'];

// Count-up hook
function useCountUp(target: number, duration: number, active: boolean): number {
  const [count, setCount] = useState(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!active || target === 0) {
      setCount(target);
      return;
    }
    const startTime = performance.now();
    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // easeOut cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(eased * target));
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      }
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [target, duration, active]);

  return count;
}

function StatCard({ stat, loading, animateActive }: { stat: DynamicStat; loading: boolean; animateActive: boolean }) {
  const isRating = stat.label === 'Average Rating';
  const isStatic = stat.isStatic;

  // Parse numeric value for count-up
  const numericTarget = stat.numericValue !== undefined
    ? stat.numericValue
    : (!isRating && !isStatic ? parseInt(stat.value.replace(/[^0-9]/g, ''), 10) || 0 : 0);

  const countedValue = useCountUp(
    numericTarget,
    1000,
    !isStatic && !isRating && animateActive && !loading
  );

  const displayValue = isStatic
    ? stat.value
    : isRating
    ? stat.value
    : countedValue.toLocaleString() + (stat.value.includes('+') ? '+' : '');

  if (loading) {
    return (
      <div className="text-center">
        {/* Icon skeleton */}
        <div
          className="w-12 h-12 rounded-2xl mx-auto mb-4 skeleton-shimmer"
          style={{ background: 'var(--bg-secondary)' }}
        />
        {/* Value skeleton */}
        <div
          className="h-10 lg:h-12 rounded-xl mx-auto mb-2 skeleton-shimmer"
          style={{ background: 'var(--bg-secondary)', width: '70%' }}
        />
        {/* Label skeleton */}
        <div
          className="h-4 rounded-lg mx-auto skeleton-shimmer"
          style={{ background: 'var(--bg-secondary)', width: '55%' }}
        />
      </div>
    );
  }

  return (
    <div className="reveal text-center">
      <div
        className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-4"
        style={{ background: 'var(--accent-light)', color: 'var(--accent)' }}>
        <Icon name={stat.icon as 'StarIcon'} size={22} />
      </div>
      <p
        className="font-display text-4xl lg:text-5xl font-semibold mb-1"
        style={{ color: 'var(--text-primary)' }}>
        {displayValue}
      </p>
      <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
        {stat.label}
      </p>
    </div>
  );
}

function TestimonialCard({ t }: { t: DynamicTestimonial }) {
  const [expanded, setExpanded] = useState(false);
  const hasSummary = !!t.summary;
  const displayText = expanded || !hasSummary ? t.quote : t.summary!;

  return (
    <div
      className="reveal rounded-2xl p-6"
      style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
      }}>
      <div className="flex items-center gap-0.5 mb-4">
        {[1, 2, 3, 4, 5].map((s) =>
          <Icon key={s} name="StarIcon" size={14} variant="solid" className="star-filled" />
        )}
      </div>
      <blockquote
        className="text-sm leading-relaxed mb-2"
        style={{ color: 'var(--text-secondary)' }}>
        &ldquo;{displayText}&rdquo;
      </blockquote>
      {hasSummary && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-xs font-medium mb-3 transition-colors"
          style={{ color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
          {expanded ? 'Show less' : 'Read more…'}
        </button>
      )}
      <div className="flex items-center gap-3 pt-4" style={{ borderTop: '1px solid var(--border)' }}>
        <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center"
          style={{ background: 'var(--accent-light)', color: 'var(--accent)' }}>
          {t.avatar ? (
            <AppImage
              src={t.avatar}
              alt={t.avatarAlt}
              width={40}
              height={40}
              className="object-cover" />
          ) : (
            <Icon name="UserIcon" size={18} />
          )}
        </div>
        <div>
          <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
            {t.name}
          </p>
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
            {t.role}
          </p>
        </div>
        <span
          className="ml-auto badge"
          style={{ background: 'var(--accent-light)', color: 'var(--accent)' }}>
          {t.platform}
        </span>
      </div>
    </div>
  );
}

function TestimonialSkeleton() {
  return (
    <div
      className="rounded-2xl p-6"
      style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
      <div className="flex gap-0.5 mb-4">
        {[1,2,3,4,5].map(s => <div key={s} className="w-3.5 h-3.5 rounded skeleton-shimmer" style={{ background: 'var(--bg-secondary)' }} />)}
      </div>
      <div className="space-y-2 mb-5">
        <div className="h-3 rounded skeleton-shimmer" style={{ background: 'var(--bg-secondary)', width: '100%' }} />
        <div className="h-3 rounded skeleton-shimmer" style={{ background: 'var(--bg-secondary)', width: '85%' }} />
        <div className="h-3 rounded skeleton-shimmer" style={{ background: 'var(--bg-secondary)', width: '70%' }} />
      </div>
      <div className="flex items-center gap-3 pt-4" style={{ borderTop: '1px solid var(--border)' }}>
        <div className="w-10 h-10 rounded-full skeleton-shimmer" style={{ background: 'var(--bg-secondary)' }} />
        <div className="space-y-1">
          <div className="h-3 w-24 rounded skeleton-shimmer" style={{ background: 'var(--bg-secondary)' }} />
          <div className="h-2 w-16 rounded skeleton-shimmer" style={{ background: 'var(--bg-secondary)' }} />
        </div>
      </div>
    </div>
  );
}

export default function SocialProofSection({ dynamicStats, statsLoading = false }: SocialProofSectionProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const [animateActive, setAnimateActive] = useState(false);
  const [testimonials, setTestimonials] = useState<DynamicTestimonial[]>([]);
  const [testimonialsLoading, setTestimonialsLoading] = useState(true);

  const stats = dynamicStats || DEFAULT_STATS;

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.1 }
    );
    sectionRef.current?.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [testimonials]);

  // Trigger count-up once data is loaded and section is visible
  useEffect(() => {
    if (!statsLoading && dynamicStats) {
      const timer = setTimeout(() => setAnimateActive(true), 100);
      return () => clearTimeout(timer);
    }
  }, [statsLoading, dynamicStats]);

  // Fetch dynamic reviews
  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        setTestimonialsLoading(true);
        const res = await fetch('/api/reviews/homepage');
        if (!res.ok) {
          // Silently fail — show empty testimonials instead of crashing
          setTestimonials([]);
          return;
        }
        const json = await res.json();
        setTestimonials(json.data || []);
      } catch (err) {
        // Network error or API unavailable — fail silently
        setTestimonials([]);
      } finally {
        setTestimonialsLoading(false);
      }
    };
    fetchTestimonials();
  }, []);

  return (
    <section ref={sectionRef} style={{ background: 'var(--bg-primary)' }}>
      {/* Stats */}
      <div className="py-20 border-y" style={{ borderColor: 'var(--border)' }}>
        <div className="max-w-8xl mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat) => (
              <StatCard
                key={stat.label}
                stat={stat}
                loading={statsLoading}
                animateActive={animateActive}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Marquee */}
      <div
        className="py-5 overflow-hidden border-b"
        style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border)' }}
        aria-hidden="true">
        
        <div className="marquee-inner">
          {[...marqueeItems, ...marqueeItems].map((item, i) =>
          <span
            key={`${item}-${i}`}
            className="mx-8 text-sm font-semibold uppercase tracking-widest whitespace-nowrap"
            style={{ color: 'var(--text-muted)' }}>
            
              {item}
              <span className="ml-8 opacity-30">✶</span>
            </span>
          )}
        </div>
      </div>

      {/* Testimonials */}
      <div className="py-24 lg:py-32">
        <div className="max-w-8xl mx-auto px-6">
          <div className="reveal text-center mb-14">
            <span
              className="text-2xs font-bold uppercase tracking-[0.2em] mb-3 block"
              style={{ color: 'var(--accent)' }}>
              
              Customer Stories
            </span>
            <h2
              className="font-display font-light text-4xl lg:text-6xl"
              style={{ color: 'var(--text-primary)' }}>
              
              Loved by{' '}
              <span className="italic">builders.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonialsLoading ? (
              [1, 2, 3].map((i) => <TestimonialSkeleton key={i} />)
            ) : testimonials.length > 0 ? (
              testimonials.map((t) => <TestimonialCard key={t.id} t={t} />)
            ) : (
              // Fallback: show empty state gracefully
              <div className="col-span-3 text-center py-8" style={{ color: 'var(--text-muted)' }}>
                <Icon name="StarIcon" size={32} className="mx-auto mb-2" style={{ color: 'var(--text-muted)' }} />
                <p className="text-sm">No reviews yet. Be the first to leave a review!</p>
              </div>
            )}
          </div>

          {testimonials.length > 0 && (
            <div className="text-center mt-10">
              <a
                href="/reviews"
                className="btn-secondary inline-flex items-center gap-2 text-sm"
              >
                <Icon name="StarIcon" size={16} />
                See All Reviews
              </a>
            </div>
          )}
        </div>
      </div>
    </section>);

}